import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPaperPlane, FaRegTimesCircle } from "react-icons/fa";
import {
  SignedDocumentDto,
  useCancelSignedDocumentMutation,
  useChangeSignedDocumentDeadlineMutation,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../../../common/formatting/displayDateWithTime";
import { DateTimeSelector } from "../../../../../common/input/DateTimeSelectors/DateTimeSelector";
import { signingDeadlineError } from "./CreateSignedDocument";
import { DocumentSignerStatusTag } from "./DocumentSignerStatusTag";

interface Props {
  contractId: string;
  signedDocument: SignedDocumentDto;
  onClose: () => void;
  onCancelled?: () => void | Promise<void>;
  onResend?: (signedDocument: SignedDocumentDto) => void;
}

export const DocumentSignersModal = ({ contractId, signedDocument, onClose, onCancelled, onResend }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const displayer = useApiError();

  const [cancel, { isLoading }] = useCancelSignedDocumentMutation();
  const [changeDeadline, { isLoading: isChangingDeadline }] = useChangeSignedDocumentDeadlineMutation();
  const [newDeadline, setNewDeadline] = useState<string>();

  const isOpenForSigning =
    !signedDocument.signingCompleted && signedDocument.status !== "Canceled" && signedDocument.status !== "Expired";
  const isResent = !!signedDocument.resentAsSignedDocumentId;

  const deadlineError = newDeadline ? signingDeadlineError(newDeadline) : undefined;
  const canSaveDeadline = !!newDeadline && !deadlineError && newDeadline !== signedDocument.deadline;

  const saveDeadline = async () => {
    if (!newDeadline) return;
    try {
      await changeDeadline({
        contractId,
        signedDocumentId: signedDocument.id,
        changeSignedDocumentDeadlineRequest: { deadline: newDeadline },
      }).unwrap();
      toast({ variant: "subtle", status: "success", title: t("The deadline is changed and the signers are notified") });
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t("Status for signers")}</Text>
          <Text fontSize="sm" fontWeight="normal">
            {signedDocument.deadline && `${t("Deadline")}: ${displayDateWithTime(signedDocument.deadline, t("at"))}`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          <Flex border="1px solid" borderColor="smBorder" rounded="md" pb="2.5">
            <Table variant="simple" borderColor="red" size="sm">
              <Thead>
                <Tr>
                  <Th borderColor="smBorder">{t("Name")}</Th>
                  <Th borderColor="smBorder">{t("Email")}</Th>
                  <Th borderColor="smBorder">{t("Status")}</Th>
                  <Th borderColor="smBorder"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {signedDocument.signers.map((e) => {
                  return (
                    <Tr key={e.id}>
                      <Td borderColor="smBorder">{`${e.firstName} ${e.lastName}`}</Td>
                      <Td borderColor="smBorder">{e.email}</Td>
                      <Td borderColor="smBorder">
                        <DocumentSignerStatusTag status={e.status} />
                      </Td>
                      <Td borderColor="smBorder">
                        {e.statusTimestamp ? new Date(e.statusTimestamp).toLocaleString() : ""}
                      </Td>
                    </Tr>
                  );
                })}
                {signedDocument.signers.length === 0 && (
                  <Tr>
                    <Td colSpan={3} width={"100%"} textAlign="center" borderColor="smBorder">
                      {t("No signers found")}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Flex>
          {isOpenForSigning && (
            <Flex justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap="4" pt="5">
              <FormControl id="newDeadline" width="auto" isInvalid={deadlineError !== undefined}>
                <FormLabel fontSize="sm" htmlFor="newDeadline">
                  {t("Change the deadline")}
                </FormLabel>
                <Flex gap="2" alignItems="center" flexWrap="wrap">
                  <DateTimeSelector defaultDate={signedDocument.deadline ?? undefined} onChange={setNewDeadline} />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    isDisabled={!canSaveDeadline}
                    isLoading={isChangingDeadline}
                    onClick={saveDeadline}
                  >
                    {t("Save new deadline")}
                  </Button>
                </Flex>
                <FormHelperText fontSize="xs">
                  {t("At most 45 days from now. The signers get an email with the new deadline.")}
                </FormHelperText>
                <FormErrorMessage>{deadlineError}</FormErrorMessage>
              </FormControl>
              <Button
                size="xs"
                colorScheme="red"
                rightIcon={<Icon as={FaRegTimesCircle} />}
                isLoading={isLoading}
                onClick={async () => {
                  if (!contractId) throw Error("Undefined contract id");
                  const res = await cancel({
                    contractId,
                    cancelSignedDocumentRequest: {
                      documentId: signedDocument.id,
                      reason: t("The signing order was cancelled by the contract owner") ?? "",
                    },
                  });
                  if ("error" in res) {
                    displayer.trigger(res.error);
                    return;
                  }
                  onCancelled?.();
                  toast({ variant: "subtle", status: "info", title: t("Cancelled") });
                  onClose();
                }}
              >
                {t("Cancel signing")}
              </Button>
            </Flex>
          )}
          {signedDocument.status === "Expired" && isResent && (
            <Text fontSize="sm" color="smMuted" pt="5">
              {t("A new signing request has been sent for this document")}
            </Text>
          )}
          {signedDocument.status === "Expired" && !isResent && onResend && (
            <Flex justifyContent="end" pt="5">
              <Button
                size="xs"
                colorScheme="blue"
                rightIcon={<Icon as={FaPaperPlane} />}
                onClick={() => onResend(signedDocument)}
              >
                {t("Resend to the same signers")}
              </Button>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
